import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Typography, Button, Select, Space, Spin, message, Row, Col } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { 
  ControlOutlined, 
  UserOutlined, 
  CalendarOutlined, 
  DownloadOutlined,
  ShareAltOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import PublicHeader from '../../components/Layout/PublicHeader';
import '../../components/Theme/PricingTableTheme.css';
import '../Home/HomePage.css';
import './PricingTable.css';

const { Title, Text } = Typography;
const { Option } = Select;

interface GameType {
  id: number;
  name: string;
  display_name: string;
  icon_url?: string;
}

interface PricingInfo {
  id: number;
  game_type_id: number;
  game_name: string;
  max_players: number;
  billing_period: string;
  price: number;
  is_active: number;
}

interface GroupedPricing {
  [gameName: string]: {
    gameTypeId: number;
    gameName: string;
    players: {
      [maxPlayers: number]: {
        [billingPeriod: string]: PricingInfo;
      };
    };
  };
}

const PricingTable: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { siteConfig } = useSiteConfig();
  const [gameTypes, setGameTypes] = useState<GameType[]>([]);
  const [pricingData, setPricingData] = useState<PricingInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGameType, setSelectedGameType] = useState<number | 'all'>('all');

  const billingPeriods = [
    { key: 'weekly', label: '周付', color: '#52c41a', days: 7 },
    { key: 'monthly', label: '月付', color: '#1890ff', days: 30 },
    { key: 'quarterly', label: '季付', color: '#722ed1', days: 90 },
    { key: 'semiannual', label: '半年付', color: '#fa8c16', days: 180 },
    { key: 'yearly', label: '年付', color: '#eb2f96', days: 365 }
  ];

  useEffect(() => {
    loadData();
    
    // 检查URL参数
    const gameParam = searchParams.get('game');
    if (gameParam) {
      const gameId = parseInt(gameParam);
      if (!isNaN(gameId)) {
        setSelectedGameType(gameId);
      }
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // 并行加载数据
      const [gameTypesRes, pricingRes] = await Promise.all([
        api.get('/games/types'),
        api.get('/admin/pricing/public')
      ]);

      setGameTypes(gameTypesRes.data);
      setPricingData(pricingRes.data);
    } catch (error: any) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 按游戏类型分组数据
  const groupedPricingData = pricingData.reduce((acc, item) => {
    if (item.is_active === 0) return acc; // 只显示启用的价格
    
    const gameName = item.game_name;
    if (!acc[gameName]) {
      acc[gameName] = {
        gameTypeId: item.game_type_id,
        gameName: gameName,
        players: {}
      };
    }
    
    if (!acc[gameName].players[item.max_players]) {
      acc[gameName].players[item.max_players] = {};
    }
    
    acc[gameName].players[item.max_players][item.billing_period] = item;
    return acc;
  }, {} as GroupedPricing);

  // 获取要显示的游戏数据
  const getDisplayData = () => {
    if (selectedGameType === 'all') {
      return groupedPricingData;
    }
    
    const selectedGame = gameTypes.find(gt => gt.id === selectedGameType);
    if (selectedGame && groupedPricingData[selectedGame.display_name]) {
      return { [selectedGame.display_name]: groupedPricingData[selectedGame.display_name] };
    }
    
    return {};
  };

  const displayData = getDisplayData();

  // 为每个游戏生成表格数据
  const generateGameTableData = (gameData: any) => {
    const allPlayers = Object.keys(gameData.players).map(Number).sort((a, b) => a - b);
    
    return allPlayers.map(players => ({
      key: players,
      players: players,
      ...billingPeriods.reduce((acc, period) => {
        const pricing = gameData.players[players]?.[period.key];
        acc[period.key] = pricing ? {
          price: pricing.price,
          gameName: gameData.gameName,
          gameTypeId: gameData.gameTypeId
        } : null;
        return acc;
      }, {} as any)
    }));
  };

  // 计算折扣
  const calculateDiscount = (price: number, period: string) => {
    const periodDays = billingPeriods.find(p => p.key === period)?.days || 30;
    const expectedPrice = (price / 30) * periodDays;
    const discount = ((expectedPrice - price) / expectedPrice) * 100;
    
    return discount > 0 ? Math.round(discount) : 0;
  };


  // 打印价格表
  const handlePrint = () => {
    window.print();
  };

  // 分享价格表
  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      message.success('链接已复制到剪贴板');
    } catch (error) {
      message.error('复制失败，请手动复制链接');
    }
  };

  // 下载价格表
  const handleDownload = () => {
    const element = document.getElementById('pricing-table');
    if (element) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>${siteConfig?.site_name || '游戏服务器'} - 价格表</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                .header { text-align: center; margin-bottom: 30px; }
                .logo { max-width: 100px; margin-bottom: 10px; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
                th { background-color: #f5f5f5; font-weight: bold; }
                .game-name { font-weight: bold; color: #1890ff; }
                .price { font-weight: bold; color: #52c41a; }
                .discount { color: #ff4d4f; font-size: 12px; }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>${siteConfig?.site_name || '游戏服务器'} - 价格表</h1>
                <p>生成时间: ${new Date().toLocaleString()}</p>
              </div>
              ${element.outerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };


  if (loading) {
    return (
      <div className="pricing-table-loading">
        <Spin size="large" />
        <Text style={{ marginTop: 16, display: 'block' }}>加载价格数据中...</Text>
      </div>
    );
  }

  return (
    <div className="homepage-container">
      {/* 公共页头 */}
      <PublicHeader currentPage="pricing" />

      {/* 页面背景 */}
      <div className="hero-background">
        <div className="hero-particles"></div>
        <div className="hero-grid"></div>
      </div>

      {/* 主要内容区域 */}
      <div className="pricing-content">
        {/* 页头卡片 */}
        <Card className="pricing-header-card">
          <div className="pricing-header-content">
            <div className="header-text">
              <Title level={2} className="pricing-title">
                <span className="gradient-text">游戏服务器价格表</span>
              </Title>
              <Text className="pricing-subtitle">
                透明定价，多种计费周期，选择最适合您的方案
              </Text>
            </div>
            
            <div className="header-actions">
              <Space>
                <Button 
                  icon={<ShareAltOutlined />} 
                  onClick={handleShare}
                  type="default"
                >
                  分享
                </Button>
                <Button 
                  icon={<PrinterOutlined />} 
                  onClick={handlePrint}
                  type="default"
                >
                  打印
                </Button>
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={handleDownload}
                  type="primary"
                >
                  下载
                </Button>
              </Space>
            </div>
          </div>
        </Card>

      {/* 筛选区域 */}
      <Card className="filter-card">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <div className="filter-item">
              <Text strong>选择游戏类型：</Text>
              <Select
                value={selectedGameType}
                onChange={setSelectedGameType}
                style={{ width: '100%', marginTop: 8 }}
                size="large"
              >
                <Option value="all">
                  <ControlOutlined /> 全部游戏
                </Option>
                {gameTypes.map(game => (
                  <Option key={game.id} value={game.id}>
                    {game.icon_url && (
                      <img 
                        src={game.icon_url} 
                        alt={game.display_name}
                        style={{ width: 16, height: 16, marginRight: 8 }}
                      />
                    )}
                    {game.display_name}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="filter-item">
              <Text strong>价格说明：</Text>
              <div style={{ marginTop: 8 }}>
                <Tag color="green">绿色</Tag> 推荐价格
                <Tag color="blue" style={{ marginLeft: 8 }}>蓝色</Tag> 标准价格
                <Tag color="red" style={{ marginLeft: 8 }}>红色</Tag> 优惠价格
              </div>
            </div>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <div className="filter-item">
              <Text strong>更新时间：</Text>
              <div style={{ marginTop: 8, color: '#666' }}>
                {new Date().toLocaleString()}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 价格表 */}
      <div id="pricing-table">
        {Object.keys(displayData).length === 0 ? (
          <Card className="pricing-table-card">
            <div className="no-data">
              <Text type="secondary">暂无价格数据</Text>
            </div>
          </Card>
        ) : (
          Object.values(displayData).map((gameData: any) => {
            const gameTableData = generateGameTableData(gameData);
            return (
              <Card 
                key={gameData.gameTypeId} 
                className="pricing-table-card"
                style={{ marginBottom: 24 }}
              >
                <div className="game-header">
                  <div className="game-info">
                    <img 
                      src={`/game_icons/${gameData.gameTypeId}.png`} 
                      alt={gameData.gameName}
                      className="game-icon"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <Title level={3} className="table-title">
                      <ControlOutlined />
                      {gameData.gameName}
                    </Title>
                  </div>
                  <div className="game-stats">
                    <Tag color="blue">{Object.keys(gameData.players).length} 种配置</Tag>
                    <Tag color="green">最低 ¥{Math.min(...Object.values(gameData.players).flatMap((p: any) => Object.values(p).map((price: any) => parseFloat(price.price))).filter(p => p > 0))}</Tag>
                  </div>
                </div>
                
                <Table
                  dataSource={gameTableData}
                  pagination={false}
                  size="middle"
                  bordered
                  className="pricing-table"
                  scroll={{ x: 600 }}
                >
                  <Table.Column
                    title={
                      <div className="column-header">
                        <UserOutlined />
                        <span>最大玩家数</span>
                      </div>
                    }
                    dataIndex="players"
                    key="players"
                    width={120}
                    fixed="left"
                    render={(players: number) => (
                      <div className="players-cell">
                        <UserOutlined />
                        <span>{players}人</span>
                      </div>
                    )}
                  />
                  
                  {billingPeriods.map(period => (
                    <Table.Column
                      key={period.key}
                      title={
                        <div className="column-header">
                          <CalendarOutlined />
                          <span>{period.label}</span>
                          <div className="period-days">{period.days}天</div>
                        </div>
                      }
                      dataIndex={period.key}
                      width={150}
                      render={(pricing: any) => (
                        <div className="pricing-cell">
                          {pricing ? (
                            <div className="price-item">
                              <div className="price-row">
                                <span className="price">¥{pricing.price}</span>
                                {calculateDiscount(pricing.price, period.key) > 0 && (
                                  <span className="discount">-{calculateDiscount(pricing.price, period.key)}%</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <Text type="secondary">-</Text>
                          )}
                        </div>
                      )}
                    />
                  ))}
                </Table>
              </Card>
            );
          })
        )}
      </div>

        {/* 底部说明 */}
        <section className="footer-section">
          <div className="footer-content">
            <Row gutter={[24, 16]}>
              <Col xs={24} md={8}>
                <div className="footer-item">
                  <Title level={4}>服务特色</Title>
                  <ul>
                    <li>7x24小时技术支持</li>
                    <li>99.9%在线率保证</li>
                    <li>全球多节点部署</li>
                    <li>一键MOD安装</li>
                  </ul>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div className="footer-item">
                  <Title level={4}>支付方式</Title>
                  <ul>
                    <li>支付宝/微信支付</li>
                    <li>银行卡转账</li>
                    <li>PayPal国际支付</li>
                    <li>加密货币支付</li>
                  </ul>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div className="footer-item">
                  <Title level={4}>联系我们</Title>
                  <ul>
                    <li>客服QQ: 3969347</li>
                    <li>客服微信: saaawl</li>
                    <li>技术支持: admin@uun.cc</li>
                    <li>官方网站: www.uun.cc</li>
                  </ul>
                </div>
              </Col>
            </Row>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PricingTable;
